// hooks/utils/transcriptCacheUtils.js
//
// Single writer for a transcript cache entry's excerpt MEMBERSHIP.
//
// The transcript entry stores `excerpt_ids` only. Excerpt objects live
// solely in the per-excerpt detail caches (useExcerpt is the ground truth —
// see the excerpt architecture notes in CLAUDE.md), so codings changes
// never touch the transcript entry at all. What these helpers manage is
// which excerpts BELONG to a transcript: optimistic creates and their
// rollbacks, deletes, and the optimistic→real id swap.
//
// ORDERING INVARIANT: an excerpt's detail entry must exist BEFORE its id
// enters membership. Consumers resolve ids through the detail caches; a
// REAL (numeric) id with no entry is an invariant violation that is
// reported to Sentry (reportMissingExcerptDetails in excerpts/queries) and
// rendered as absent — nothing refetches it. An id added ahead of its
// detail entry would fire that report spuriously. Every caller writes the
// detail entry first; keep it that way.

import { notifyManager } from '@tanstack/react-query';
import { transcriptKeys } from '../transcripts/queries';
// Function-scope use only: this import participates in a module cycle
// (excerpts/queries -> this file -> transcripts/queries -> excerpts/queries)
// that is safe precisely because nothing here touches the binding at
// module-eval time.
import { excerptKeys } from '../excerpts/queries';
import { normalizeId } from './idNormalization';

// THE seeding primitive: write server excerpts into the detail caches —
// skipped for a cancelled fetch (the correctness-critical guard pinned by
// queries.cancellationRace.test.js) — and heal membership for Transcript
// excerpts a cached transcript entry is missing (a dropped cable broadcast
// is the only way that happens; the base branch's bulk writers healed it
// as a side effect, and losing it made list pages see excerpts their
// transcript page then omitted). Every seeding site uses this ONE body:
// four hand-kept copies of the guard is how the next divergence starts.
// Options:
// - healMembership: append ids a cached transcript's membership is missing
//   (dropped-cable healing), BATCHED to one write per transcript. Callers
//   seeding inside useTranscript's own queryFn pass false — every write
//   there targets the stale entry the queryFn's return value replaces.
export const seedExcerptDetails = (queryClient, excerpts, signal, options = {}) => {
  const { healMembership = true } = options;
  if (signal?.aborted) return;
  const healByTranscript = new Map();
  // One notification flush for the whole response: each setQueryData
  // otherwise schedules its own flush, and a 100-excerpt page against an
  // 800-observer resolution re-ran combine per entry — the notification
  // storm the deleted bulk writer's batching existed to prevent.
  notifyManager.batch(() => {
  (excerpts || []).forEach((excerpt) => {
    queryClient.setQueryData(excerptKeys.detail(excerpt.id), excerpt);
    if (healMembership && excerpt?.codeable_type === 'Transcript' && excerpt.transcript_id != null) {
      const transcriptId = normalizeId(excerpt.transcript_id);
      if (!healByTranscript.has(transcriptId)) healByTranscript.set(transcriptId, []);
      healByTranscript.get(transcriptId).push(excerpt.id);
    }
  });
  healByTranscript.forEach((seededIds, transcriptId) => {
    const { normalizedTranscriptId, transcript } = readIds(queryClient, transcriptId);
    if (!transcript) return;
    const members = new Set(transcript.excerpt_ids.map((id) => normalizeId(id)));
    const additions = seededIds.filter((id) => !members.has(normalizeId(id)));
    if (!additions.length) return;
    writeIds(queryClient, normalizedTranscriptId, transcript, [...transcript.excerpt_ids, ...additions.map(normalizeId)]);
  });
  });
};

const readIds = (queryClient, transcriptId) => {
  const normalizedTranscriptId = normalizeId(transcriptId);
  if (normalizedTranscriptId == null) return {};
  const transcript = queryClient.getQueryData(transcriptKeys.detail(normalizedTranscriptId));
  if (!transcript || !Array.isArray(transcript.excerpt_ids)) return {};
  return { normalizedTranscriptId, transcript };
};

const writeIds = (queryClient, normalizedTranscriptId, transcript, excerpt_ids) => {
  queryClient.setQueryData(transcriptKeys.detail(normalizedTranscriptId), { ...transcript, excerpt_ids });
};

// Add one excerpt id to a transcript's membership (no-op when already a
// member, when the transcript isn't cached, or when its entry doesn't
// carry excerpt_ids yet). Pass the excerpt OBJECT as `excerpt` to make the
// ordering invariant hold by construction: the detail entry is seeded
// before the id enters membership, so no future call site can violate it
// by discipline lapse.
export const addExcerptIdToTranscript = (queryClient, transcriptId, excerptId, excerpt = null) => {
  if (excerpt) {
    queryClient.setQueryData(excerptKeys.detail(excerpt.id), excerpt);
  }
  const { normalizedTranscriptId, transcript } = readIds(queryClient, transcriptId);
  if (!transcript) return;
  const normalizedExcerptId = normalizeId(excerptId);
  if (transcript.excerpt_ids.some((id) => normalizeId(id) === normalizedExcerptId)) return;
  writeIds(queryClient, normalizedTranscriptId, transcript, [...transcript.excerpt_ids, normalizedExcerptId]);
};

// Drop one excerpt id (create-excerpt rollback, excerpt deletion).
export const removeExcerptIdFromTranscript = (queryClient, transcriptId, excerptId) => {
  const { normalizedTranscriptId, transcript } = readIds(queryClient, transcriptId);
  if (!transcript) return;
  const normalizedExcerptId = normalizeId(excerptId);
  const excerpt_ids = transcript.excerpt_ids.filter((id) => normalizeId(id) !== normalizedExcerptId);
  if (excerpt_ids.length === transcript.excerpt_ids.length) return;
  writeIds(queryClient, normalizedTranscriptId, transcript, excerpt_ids);
};

// Swap an optimistic client id for the server's id when a create settles.
// Deduplicates: the server can reuse an EXISTING excerpt for an identical
// span (find_or_create_by in excerpts_with_codes_controller), so when the
// real id is already a member the optimistic id is simply dropped.
//
// Replace-or-APPEND, not replace-only: a concurrent transcript refetch can
// wholesale-replace excerpt_ids inside the create window (its payload
// predates the POST, so it carries neither the optimistic id nor the real
// one) — the real id must still land, or the new excerpt stays invisible
// to every membership consumer until the next refetch. The deleted bulk
// writer healed exactly this race by replace-or-appending.
export const replaceExcerptIdInTranscript = (queryClient, transcriptId, fromExcerptId, toExcerptId) => {
  const { normalizedTranscriptId, transcript } = readIds(queryClient, transcriptId);
  if (!transcript) return;
  const normalizedFrom = normalizeId(fromExcerptId);
  const normalizedTo = normalizeId(toExcerptId);
  const hasFrom = transcript.excerpt_ids.some((id) => normalizeId(id) === normalizedFrom);
  const alreadyMember = transcript.excerpt_ids.some((id) => normalizeId(id) === normalizedTo);
  if (!hasFrom) {
    if (!alreadyMember) {
      writeIds(queryClient, normalizedTranscriptId, transcript, [...transcript.excerpt_ids, normalizedTo]);
    }
    return;
  }
  const excerpt_ids = transcript.excerpt_ids
    .filter((id) => !(alreadyMember && normalizeId(id) === normalizedFrom))
    .map((id) => (normalizeId(id) === normalizedFrom ? normalizedTo : id));
  writeIds(queryClient, normalizedTranscriptId, transcript, excerpt_ids);
};

// The ids of every cached transcript whose membership contains this
// excerpt. Excerpt ids are globally unique (one excerpts table), so this is
// an exact ownership lookup — used to cancel ONLY the owning transcript's
// in-flight refetch when the detail cache can't supply the transcript id.
export const findTranscriptIdsContainingExcerpt = (queryClient, excerptId) => {
  const normalizedExcerptId = normalizeId(excerptId);
  const ids = [];
  queryClient.getQueriesData({ queryKey: transcriptKeys.details() }).forEach(([key, transcript]) => {
    if (!Array.isArray(transcript?.excerpt_ids)) return;
    if (transcript.excerpt_ids.some((id) => normalizeId(id) === normalizedExcerptId)) {
      ids.push(key[key.length - 1]);
    }
  });
  return ids;
};
